import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Paperclip, FileText, Image as ImageIcon, X, Download } from "lucide-react";

/**
 * TaskAttachments: reusable upload + list + download component.
 *
 * Props:
 *   taskId: current task id
 *   scope: "auth" | "board"      — which endpoints to hit
 *   boardToken: share token (required when scope="board")
 *   canDelete: whether the current viewer can delete an attachment
 */
export default function TaskAttachments({ taskId, scope = "auth", boardToken, canDelete = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const blobUrlsRef = useRef({});

  const listUrl = scope === "board"
    ? `/public/board/${boardToken}/tasks/${taskId}/attachments`
    : `/tasks/${taskId}/attachments`;
  const uploadUrl = listUrl;

  const load = async () => {
    try {
      const config = scope === "board"
        ? { headers: (() => {
            const raw = localStorage.getItem(`board_session_${boardToken}`);
            return raw ? { "X-Board-Session": raw } : {};
          })() }
        : undefined;
      const { data } = await api.get(listUrl, config);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); return () => {
    Object.values(blobUrlsRef.current).forEach((u) => URL.revokeObjectURL(u));
    blobUrlsRef.current = {};
  // eslint-disable-next-line
  }; }, [taskId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (scope === "board") {
        const raw = localStorage.getItem(`board_session_${boardToken}`);
        if (raw) config.headers["X-Board-Session"] = raw;
      }
      const { data } = await api.post(uploadUrl, form, config);
      setItems((p) => [...p, data]);
      toast.success("Uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (a) => {
    try {
      const config = { responseType: "blob" };
      if (scope === "board") {
        const raw = localStorage.getItem(`board_session_${boardToken}`);
        if (raw) config.headers = { "X-Board-Session": raw };
      }
      const resp = await api.get(`/attachments/${a.id}/file`, config);
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.filename}"?`)) return;
    try {
      await api.delete(`/attachments/${a.id}`);
      setItems((p) => p.filter((x) => x.id !== a.id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // For images, fetch as blob and set src via object URL so the auth header can be sent.
  useEffect(() => {
    items.forEach(async (a) => {
      if (blobUrlsRef.current[a.id]) return;
      if (!a.content_type?.startsWith("image/")) return;
      try {
        const config = { responseType: "blob" };
        if (scope === "board") {
          const raw = localStorage.getItem(`board_session_${boardToken}`);
          if (raw) config.headers = { "X-Board-Session": raw };
        }
        const resp = await api.get(`/attachments/${a.id}/file`, config);
        blobUrlsRef.current[a.id] = URL.createObjectURL(resp.data);
        // trigger re-render
        setItems((prev) => [...prev]);
      } catch { /* ignore, thumbnail just won't show */ }
    });
  // eslint-disable-next-line
  }, [items]);

  return (
    <div className="space-y-2" data-testid="attachments-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-700">Attachments {items.length > 0 && <span className="text-slate-400 font-normal">· {items.length}</span>}</p>
        <label>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid="attachment-file-input" />
          <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="attachment-upload-btn">
            <Paperclip size={12} className="mr-1.5" /> {uploading ? "Uploading…" : "Add file"}
          </Button>
        </label>
      </div>
      {loading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No attachments yet. Images and PDFs up to 10 MB.</p>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="attachment-list">
          {items.map((a) => {
            const isImg = a.content_type?.startsWith("image/");
            const thumbUrl = blobUrlsRef.current[a.id];
            return (
              <li key={a.id} className="border border-slate-200 rounded overflow-hidden bg-white" data-testid={`attachment-${a.id}`}>
                <div className="h-24 bg-slate-100 flex items-center justify-center overflow-hidden">
                  {isImg && thumbUrl ? (
                    <img src={thumbUrl} alt={a.filename} className="w-full h-full object-cover" />
                  ) : isImg ? (
                    <ImageIcon size={28} className="text-slate-400" />
                  ) : (
                    <FileText size={28} className="text-slate-400" />
                  )}
                </div>
                <div className="p-2 flex items-center gap-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-slate-800 truncate" title={a.filename}>{a.filename}</p>
                    <p className="text-[10px] text-slate-500">{a.uploader_name} · {(a.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button className="p-1 text-slate-500 hover:text-slate-900" onClick={() => download(a)} title="Download" data-testid={`attachment-download-${a.id}`}>
                    <Download size={12} />
                  </button>
                  {canDelete && (
                    <button className="p-1 text-rose-500 hover:text-rose-700" onClick={() => remove(a)} title="Delete" data-testid={`attachment-delete-${a.id}`}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
